
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useCreateStaff, type UserType } from '@/api/staff';
import { useGym } from '@/contexts/gym-context';
import { useQueryClient } from '@tanstack/react-query';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface AddStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddStaffDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddStaffDialogProps) {
  const { t } = useTranslation();
  const { mutateAsync: createStaff, isPending } = useCreateStaff();
  const { gyms, selectedGym } = useGym();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const formSchema = z.object({
    name: z.string().min(2, { message: t('staff.nameRequired') }),
    surname: z.string().min(2, { message: t('staff.surnameRequired') }),
    email: z
      .string()
      .optional()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: t('staff.validEmail'),
      }),
    phone: z.string().optional(),
    userType: z.enum(['admin', 'reception', 'trainer', 'staff']),
    status: z.enum(['active', 'inactive']),
    gym: z.string().min(1, { message: t('staff.selectAtLeastOneGym') }),
    password: z
      .string()
      .optional()
      .refine((val) => !val || val.length >= 6, {
        message: t('auth.password') + ' (min 6 chars)',
      }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      userType: 'staff' as UserType,
      status: 'active',
      gym: selectedGym && selectedGym !== 'all' ? selectedGym : '',
      password: '',
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      setError(null);
      await createStaff({
        ...data,
        email: data.email || '',
        phone: data.phone || '',
        password: data.password || '',
        userType: data.userType as UserType,
        gym: [data.gym],
      });

      // Invalidate the staff query to force a refetch
      queryClient.invalidateQueries({ queryKey: ['staff'] });

      form.reset();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('staff.addFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('staff.addStaff')}</DialogTitle>
          <DialogDescription>
            {t('staff.addStaffDescription')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('staff.firstName')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('staff.firstNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('staff.lastName')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('staff.lastNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('staff.emailPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.password')} ({t('common.optional')})</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t('auth.password')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.phone')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('staff.phonePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="userType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.userType')}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('staff.selectUserType')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="staff">
                        {t('staff.userTypes.staff')}
                      </SelectItem>
                      <SelectItem value="admin">
                        {t('staff.userTypes.admin')}
                      </SelectItem>
                      <SelectItem value="reception">
                        {t('staff.userTypes.reception')}
                      </SelectItem>
                      <SelectItem value="trainer">
                        {t('staff.userTypes.trainer')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.status')}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('staff.selectStatus')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">
                        {t('staff.active')}
                      </SelectItem>
                      <SelectItem value="inactive">
                        {t('staff.inactive')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gym"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.gyms')}</FormLabel>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    {gyms && gyms.length > 0 ? (
                      gyms.map((gym) => (
                        <div
                          key={gym._id}
                          className="flex items-center space-x-3"
                        >
                          <RadioGroupItem
                            value={gym._id}
                            id={`gym-${gym._id}`}
                          />
                          <FormLabel
                            htmlFor={`gym-${gym._id}`}
                            className="font-normal cursor-pointer"
                          >
                            {gym.name}
                          </FormLabel>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t('staff.noGymsAvailable')}
                      </p>
                    )}
                  </RadioGroup>
                  <FormMessage />
                </FormItem>
              )}
            />

            {error && <div className="text-red-500 text-sm">{error}</div>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
